/**
 * HOF-219 — operator-facing copy for the DOCUMENT-level unlink.
 *
 * The action label is HOF-216's, verbatim and by decision (spec assumption 7):
 * one German phrase for both layers, distinguished by the confirmation BODY,
 * which names the Gutschrift, the Lieferschein and — the part that is specific
 * to this layer — the LINK KIND.
 *
 * The link kind is in the copy because the exclusion is axis-agnostic. A
 * ``native`` edge is an exact shared-line-ref join from wibill, so severing one
 * is overruling the source system rather than a heuristic guess; an operator
 * doing that deliberately is fine, an operator doing it without being told is
 * the failure this sentence prevents (spec Risks).
 *
 * Strings are hardcoded de-DE, matching this surface's existing convention —
 * there is no i18n key for the Gutschrift-Check screen and a half-translated
 * one would be worse than a consistently German one.
 */

/** Same label as the line-level unlink, deliberately (spec assumption 7). */
export { UNLINK_ACTION_LABEL, UNLINK_CONFIRM_CANCEL } from './unlinkConfirmation';

export const DOCUMENT_UNLINK_CONFIRM_TITLE = 'Beleg-Verknüpfung lösen?';
/**
 * Tooltip on the on-edge control. It does NOT name a keyboard shortcut, unlike
 * the line-level one: Backspace/Delete stays scoped to line edges (AC 14), so
 * advertising it here would promise a path that does nothing.
 */
export const DOCUMENT_UNLINK_HINT = 'Diese Gutschrift und diesen Lieferschein trennen';
/** The consequence the confirmation must name. */
export const DOCUMENT_UNLINK_REEVALUATION_NOTE =
  'Die Gruppe wird danach neu berechnet und kann sich in zwei Kacheln teilen.';

const UNNAMED_SETTLEMENT = 'ohne Nummer';
const UNNAMED_DELIVERY = 'ohne Nummer';

/**
 * How each ``link_kind`` reads to an operator.
 *
 * ``native`` gets the longest phrase on purpose — it is the one where "you are
 * about to overrule the source system" is the whole message. ``null`` is a real
 * case (the canvas can draw a GS→LS arrow for a pair ``gs_ls_edges`` does not
 * name), and it says so rather than guessing.
 */
export const DOCUMENT_LINK_KIND_LABELS: Record<string, string> = {
  ref: 'Lieferschein-Nr. auf der Gutschrift',
  pos: 'POS-/D-Nummer',
  'ref+pos': 'Lieferschein-Nr. und POS-/D-Nummer',
  native: 'exakter Zeilenbezug aus wibill',
};

export function formatDocumentLinkKind(linkKind: string | null | undefined): string {
  if (!linkKind) return 'unbekannt';
  return DOCUMENT_LINK_KIND_LABELS[linkKind] ?? linkKind;
}

/**
 * The confirmation body. Names the Gutschrift, the Lieferschein, what joined
 * them, and what happens next.
 *
 * Tolerates blank labels because a descriptor lookup can miss — a canvas
 * refetch can land between the right-click and the confirm — and a crash there
 * would be strictly worse than a vaguer sentence.
 */
export function buildDocumentUnlinkConfirmationMessage(params: {
  settlementLabel?: string | null;
  deliveryLabel?: string | null;
  linkKind?: string | null;
}): string {
  const settlement = params.settlementLabel?.trim() || UNNAMED_SETTLEMENT;
  const delivery = params.deliveryLabel?.trim() || UNNAMED_DELIVERY;
  return (
    `Der Lieferschein ${delivery} wird von der Gutschrift ${settlement} getrennt. ` +
    `Verknüpft über: ${formatDocumentLinkKind(params.linkKind)}. ` +
    DOCUMENT_UNLINK_REEVALUATION_NOTE
  );
}

/** ``Gutschrift 999986`` / ``Lieferschein LF99990166`` — never a bare id. */
export function formatDocumentLabel(kind: 'settlement' | 'delivery', event: Record<string, unknown> | null): string {
  const prefix = kind === 'settlement' ? 'Gutschrift' : 'Lieferschein';
  if (!event) return `${prefix} ${UNNAMED_SETTLEMENT}`;
  const candidates =
    kind === 'settlement'
      ? ['number', 'credit_note_number', 'invoice_number', 'reference']
      : ['delivery_number', 'delivery_note_reference', 'pos_reference'];
  for (const key of candidates) {
    const value = event[key];
    if (typeof value === 'string' && value.trim()) return `${prefix} ${value.trim()}`;
    if (typeof value === 'number') return `${prefix} ${value}`;
  }
  return `${prefix} ${UNNAMED_DELIVERY}`;
}
